import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import Config from "../../../../utils/config/config";
import { jwtDecode } from "jwt-decode";
import { UserServiceClientApi } from "../../../../services/api/UserApiServices";
import { resetCallInfo } from "../../../../state/actions/phone";
import { JITSI_DISABLED_DOMAIN_LIST } from "../../../../utils/constants";
import { SmallCircleButton } from "../../../../components/common/SmallCircleButton";
import { findCallerName, getFormattedDuration } from "../../components/Utils";
import { getNameAcronym } from "../../../../utils/Helpers";
import { showToast } from "../../../../components/ui/toaster";

const R = require("ramda");

const sleep = (delay) => new Promise((resolve) => setTimeout(resolve, delay));
const getMediaStream = async () => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
    return { stream, audio: true, video: false };
  } catch {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
    return { stream, audio: true, video: false };
  }
};

const webrtc_server = Config.webertcSignalServer;
const dailyPlatformUrl = Config.dailyPlatformUrl;
const daily_baseUrl = Config.daily_baseUrl;
const xApiKey = Config.dailyXApiKey;
const path = "/webrtc/peerjs/myapp";

const AudioCall = ({
  meetingId,
  onMeetingIdCreated,
  onMeetingStarted,
  onMeetingEnded,
  onError,
  callingTo,
  from,
  toName,
  callRejected,
  setVideoSessionId,
  callDuration: propCallDuration,
  options,
  url,
}) => {
    const remoteSource = useRef();
  const localSource = useRef();

  const [audioMute, setAudioMute] = useState(false);
  const [peer, setPeer] = useState(null);
  const [meetingStarted, setMeetingStarted] = useState(false);
  const [meetingEnded, setMeetingEnded] = useState(false);
  const [callStatus, setCallStatus] = useState(null);
  const [call, setCall] = useState(null);
  const [meetingIdState, setMeetingIdState] = useState(null);
  const [dataConnection, setDataConnection] = useState(null);
  const [initiator, setInitiator] = useState(false);
  const [startTimer, setStartTimer] = useState(false);
  const [callFrom, setCallFrom] = useState("");
  const [callerName, setCallerName] = useState("");
  const [localCallDuration, setLocalCallDuration] = useState(0);

  const currentUser = useSelector((state) => state.user);
  const selectedDomain = useSelector((state) => state.domain.selectedDomain);
  const conversation = useSelector((state) => state.chats.selectedConversation);
  const videoEnabled = useSelector((state) => state.phone.videoEnabled);
  const diallerState = useSelector((state) => state.phone.diallerState);
  const to = useSelector((state) => state.phone.to);
  const dispatch = useDispatch();

  const setSource = ({ source, stream }) => {
    if ("srcObject" in source.current) {
      source.current.srcObject = stream;
    } else {
      source.current.src = URL.createObjectURL(stream);
    }
  };

  const toggleAudio = () => {
    const tracks = localSource.current?.srcObject?.getTracks();
    if (!tracks) return;

    const isMuted = !audioMute;
    tracks.forEach((track) => {
      if (track.kind === "audio") {
        track.enabled = !isMuted;
      }
    });
    setAudioMute(isMuted);
  };

  const endCall = () => {
    if (dataConnection) dataConnection.send("end-call");
    if (peer) {
      peer.disconnect();
      peer.destroy();
    }
    if (localSource.current?.srcObject) {
      localSource.current.srcObject.getTracks().forEach((track) => track.stop());
    }

    setPeer(null);
    setMeetingEnded(true);
    setLocalCallDuration(0);
    setTimeout(() => {
      onMeetingEnded(meetingIdState, callStatus);
    }, 2000);
  };

  const createJitsiMeeting = async () => {
    const user = currentUser.user;
    const emailAddress = user.emailAddress;
    const userDomain = selectedDomain;
    const sessionId = currentUser.auth.sessionId;
    const intentId = "createDailyMeetingIntent";

    const participants = from === "channel" ? conversation?.channel?.participants : [`${callingTo?.toUserid}`];
    const conversationId = from === "channel" ? callingTo?.toUserid : conversation?.conversationId;

    const callUrl = `${daily_baseUrl}/createNewMeeting`;
    const headers = { "Content-Type": "application/json", "x-api-key": xApiKey };

    const data = {
      userDomain,
      data: {
        userDomain,
        conversationId,
        meetingName: "Loft Meeting",
        hostUserEmail: emailAddress,
        intentId,
        enable_chat: false,
        allowGuests: true,
        meetingType: from === "channel" ? "groupCalls" : "one2one",
        hostUserPassword: "Test@123",
        participants,
        video: videoEnabled || false,
        audio: true,
        brand: userDomain,
      },
    };

    const response = await fetch(callUrl, {
      method: "POST",
      headers,
      body: JSON.stringify(data),
    }).then((res) => res.json());

    return response;
  };

  const createJitsiConnection = async () => {
    const response = await createJitsiMeeting();
    if (response?.error) {
      showToast("error", response?.error);
      dispatch(resetCallInfo());
      return;
    }

    const jwtToken =response.jwtToken ;
    const decoded = jwtDecode(jwtToken);
    const redirectBaseUrl = `https://${decoded?.context?.user?.redirectionUrl}/`;
    const newUrl = response.url?.replace(dailyPlatformUrl, redirectBaseUrl);
    
    if (redirectBaseUrl && response.url) {
      const newTab = window.open(`${newUrl}?jwt=${jwtToken}`, "_blank");
      dispatch(resetCallInfo());
     setVideoSessionId(null);
      const interval = setInterval(() => {
        if (newTab?.closed && decoded.context?.meetingType === "one2one") {
          UserServiceClientApi.sendVoipCall({
            callerUserId: callingTo?.toUserid,
            video: videoEnabled || false,
            callAction: "CallEnd",
            userId: currentUser?.sessionInfo?.userId,
            videoSessionId: jwtToken,
            selectedDomain: selectedDomain,
          });
          clearInterval(interval);
        }
      }, 1000);

      return () => clearInterval(interval);
    }

    onMeetingIdCreated(jwtToken, options);
  };

  useEffect(() => {
    const user = currentUser.user;
    const userDomain = selectedDomain;

    if (!meetingId && !url) {
      if (!JITSI_DISABLED_DOMAIN_LIST.includes(userDomain)) {
        createJitsiConnection();
      }
    } else {
      if (!JITSI_DISABLED_DOMAIN_LIST.includes(userDomain) && meetingId) {
        const decoded = jwtDecode(meetingId);
        const redirectBaseUrl = `https://${decoded?.context?.user?.redirectionUrl}/`;
        if (redirectBaseUrl && url) {
          const replaceUrl = url.replace(dailyPlatformUrl, redirectBaseUrl);
          window.open(`${replaceUrl}?jwt=${meetingId}`, "_blank");
          dispatch(resetCallInfo());
          setVideoSessionId(null);
        }
      } else {
        setMeetingIdState(meetingId);
        setCallStatus("responder");
        setCallFrom(from);
        setInitiator(false);
        setLocalCallDuration(0);
        setStartTimer(false);
      }
    }

    const handleBeforeUnload = (e) => {
      onMeetingEnded(meetingIdState);
      e.preventDefault();
      e.returnValue = "Are you sure you want to close the meeting?";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [to]);

  useEffect(() => {
    if (callRejected) {
      endCall();
    }
  }, [callRejected]);

  useEffect(() => {
    if (propCallDuration > localCallDuration) {
      setLocalCallDuration(propCallDuration);
      setStartTimer(true);
    }
  }, [propCallDuration]);

  useEffect(() => {
    setCallFrom(from);
  }, [from]);

  const userDomain = selectedDomain;

  if (!JITSI_DISABLED_DOMAIN_LIST.includes(userDomain)) return null;

  return (
    <div className="main-audio-container" style={{ backgroundImage: "linear-gradient(#51555a,#272e35)", minHeight: "450px", height: "auto", borderRadius: "0.3rem", overflow: "hidden" }}>
      <div className="mt-5 pt-2 d-flex justify-content-center align-items-center flex-column">
        <div style={{ marginTop: "20px", color: "#ffffff", fontSize: "24px", fontWeight: "bold" }}>
          {callingTo.toUserName || callFrom}
        </div>

        {callStatus === "initiator" && !startTimer && !localCallDuration && (
          <div style={{ color: "#2FC76F", fontSize: "18px", fontWeight: "bold" }}>Calling ...</div>
        )}

        {localCallDuration > 0 && startTimer && (
          <div style={{ color: "#ffffff", fontSize: "16px" }}>{getFormattedDuration(localCallDuration)}</div>
        )}

        <div style={{ marginBottom: "2rem", marginTop: "2rem" }}>
          <img loading="lazy" height="auto" src="/img/calling-emptyavatar.png" />
        </div>

        <div className="mt-2 pt-2 d-flex justify-content-center flex-row">
          <SmallCircleButton
            backgroundColor="#2A2D3C"
            color="#fff"
            toggleDisable={localCallDuration > 0 && startTimer ? false : true}
            iconClass={audioMute ? "icon-mic-mute" : "icon-mic"}
            onClick={toggleAudio}
            className="mx-2"
            border="0.92px solid #FFFFFF"
            size={60}
          />
        </div>

        <div className="d-flex justify-content-center align-items-center mt-4 mb-4">
          <a href="#" className="disconect-button d-flex align-items-center justify-content-center m-1" onClick={endCall}>
            <img loading="lazy" alt="End Audio Call" src="/img/call-endcall-btn@2x.png" width="60" />
          </a>
        </div>
      </div>

      <audio ref={remoteSource} autoPlay className="remote-audio-source" />
      <div className="local-audio-container">
        <audio id="self" muted autoPlay ref={localSource} className="local-audio-source" />
      </div>
    </div>
  );
};


export default AudioCall;
